import CheckIcon from "@mui/icons-material/Check";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  RadioGroup,
  FormControl
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { RadioCard } from "~/components/RadioCard";

export type RequestType = "general" | "blocker";

interface RequestTypeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (requestType: RequestType) => void;
  isLoading?: boolean;
}

const REQUEST_TYPE_OPTIONS = [
  { value: "general", labelKey: "general request" },
  { value: "blocker", labelKey: "blocker" }
] as const;

export function RequestTypeModal({
  open,
  onClose,
  onConfirm,
  isLoading = false
}: RequestTypeModalProps) {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<RequestType>("general");

  const handleConfirm = () => {
    onConfirm(selectedType);
  };

  const handleClose = () => {
    if (!isLoading) {
      setSelectedType("general");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("select request type")}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("please select the type of assistance you need")}
          </Typography>

          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as RequestType)}
            >
              {REQUEST_TYPE_OPTIONS.map((option, i) => (
                <Box
                  key={option.value}
                  sx={{ mb: i < REQUEST_TYPE_OPTIONS.length - 1 ? 1 : 0 }}
                >
                  <RadioCard
                    value={option.value}
                    label={t(option.labelKey)}
                    selected={selectedType === option.value}
                    disabled={isLoading}
                  />
                </Box>
              ))}
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading} color="inherit">
          {t("cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? undefined : <CheckIcon />}
        >
          {isLoading ? t("raising hand...") : t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
